import config from '../config.js';

class ApiService {
  constructor() {
    this.baseURL = config.API_BASE_URL;
    this.token = localStorage.getItem('authToken');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('authToken');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    };

    // Ajouter le token d'authentification si disponible
    const token = this.getToken();
    if (token) {
      defaultOptions.headers['Authorization'] = `Bearer ${token}`;
    }

    if (options.body && !(options.body instanceof FormData)) {
      options.body = JSON.stringify(options.body);
    }

    if (options.body instanceof FormData) {
      delete defaultOptions.headers['Content-Type'];
    }

    const response = await fetch(url, {
      ...defaultOptions,
      ...options,
    });

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || `HTTP ${response.status}`;
      } catch {
        errorMessage = `HTTP ${response.status} - ${response.statusText}`;
      }

      // Gérer l'expiration du token seulement si ce n'est pas une tentative de login
      if (response.status === 401 && !endpoint.includes('/auth/login')) {
        this.setToken(null);
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  async login(email, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    
    if (response.success && response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async logout() {
    try {
      await this.request('/auth/logout', {
        method: 'POST',
      });
    } finally {
      this.setToken(null);
    }
  }

  async getProfile() {
    return this.request('/auth/profile');
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    });
  }

  async updateProfile(profileData) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: profileData,
    });
  }

  async getAccountInfo() {
    return this.request('/instagram/account');
  }

  async publishPost(formData) {
    // Supprimer userId du formData car il sera récupéré du token côté serveur
    formData.delete('userId');
    
    return this.request('/instagram/publish', {
      method: 'POST',
      body: formData,
    });
  }

  async publishReel(formData) {
    return this.request('/instagram/publish/reel', {
      method: 'POST',
      body: formData,
    });
  }

  async generateVideoOverlay(formData) {
    const url = `${this.baseURL}/video/generate-overlay`;
    
    const headers = {};
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Timeout de 10 minutes pour la génération vidéo (peut prendre du temps)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10 * 60 * 1000); // 10 minutes
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: formData,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || `HTTP ${response.status}`;
        } catch {
          errorMessage = `HTTP ${response.status} - ${response.statusText}`;
        }

        if (response.status === 401) {
          this.setToken(null);
          throw new Error('Session expirée. Veuillez vous reconnecter.');
        }

        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('La génération vidéo a pris trop de temps. Veuillez réessayer avec une vidéo plus courte.');
      }
      throw error;
    }
  }

  async publishCarousel(formData, sessionId) {
    const url = `${this.baseURL}/instagram/publish/carousel`;
    
    // Créer les headers avec le token d'authentification
    const headers = {};
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    headers['X-Session-ID'] = sessionId;

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || `HTTP ${response.status}`;
      } catch {
        errorMessage = `HTTP ${response.status} - ${response.statusText}`;
      }

      if (response.status === 401) {
        this.setToken(null);
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getPostsHistory() {
    return this.request('/posts/history');
  }

  async healthCheck() {
    return this.request('/health');
  }

  // Templates API
  async getTemplates(filters = {}) {
    const params = new URLSearchParams();
    if (filters.format) params.append('format', filters.format);
    if (filters.category) params.append('category', filters.category);
    if (filters.active !== undefined) params.append('active', filters.active);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/templates?${queryString}` : '/templates';
    return this.request(endpoint);
  }

  async getTemplate(id) {
    return this.request(`/templates/${id}`);
  }

  async createTemplate(templateData) {
    return this.request('/templates', {
      method: 'POST',
      body: templateData,
    });
  }

  async updateTemplate(id, templateData) {
    return this.request(`/templates/${id}`, {
      method: 'PUT',
      body: templateData,
    });
  }

  async deleteTemplate(id) {
    return this.request(`/templates/${id}`, {
      method: 'DELETE',
    });
  }
}

export default new ApiService();
